import type {
  FeedbackCreateRequest,
  FeedbackImageAttachmentRef,
} from '~/api/generated-api';

function getViewport(): { width: number; height: number } | undefined {
  if (typeof window === 'undefined') return undefined;
  return {
    width: window.innerWidth ?? 0,
    height: window.innerHeight ?? 0,
  };
}

/**
 * Coerces to a schema-valid 1–5 integer, or `undefined` if the value cannot
 * be represented in range (omitted in the request body).
 */
function normalizeStarRating(
  starRating: number | undefined
): number | undefined {
  if (starRating == null || !Number.isFinite(starRating)) {
    return undefined;
  }
  const rounded = Math.round(starRating);
  if (rounded < 1 || rounded > 5) {
    return undefined;
  }
  return rounded;
}

/** Maps UI state into the OpenAPI `FeedbackCreateRequest` body. */
export function buildFeedbackPayload(params: {
  message: string;
  /** When set, sent as 1–5; when unset, the field is omitted. */
  starRating?: number;
  attachInfo: boolean;
  userId?: string | null;
  /** Blob refs from `uploadFeedbackImage`; omitted when empty. */
  imageAttachments?: FeedbackImageAttachmentRef[];
}): FeedbackCreateRequest {
  const { message, starRating, attachInfo, userId, imageAttachments } = params;
  const payload: FeedbackCreateRequest = { feedback: message.trim() };
  const sr = normalizeStarRating(starRating);
  if (sr != null) {
    payload.starRating = sr;
  }
  if (attachInfo) {
    payload.url =
      typeof window !== 'undefined' ? window.location.href : undefined;
    if (userId) payload.userId = userId;
    payload.userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    payload.viewport = getViewport();
    payload.platform = 'WEB';
    payload.heapSessionId =
      typeof window !== 'undefined'
        ? (window?.heap?.getSessionId?.() ?? undefined)
        : undefined;
  }
  if (imageAttachments?.length) {
    payload.imageAttachments = imageAttachments;
  }
  return payload;
}
